import { useEffect, useMemo, useState } from 'react';

import styles from './OrganizationTree.module.scss';

/**
 * 조직도 트리 컴포넌트
 * - /team/{companyCode}의 teamList(평면 리스트)를 departmentCode(상위 teamCode) 기반으로 트리로 구성
 * - Figma 디자인 기반: 확장/축소 토글 + 연결 라인
 */
function OrganizationTree({
  companyName = '링크아이',
  teamList = [],
  selectedTeamCode = null,
  onSelectTeam,
  onSelectCompany,
  isCompanySelected = false,
  height = 577,
}) {
  // teamList에서 teamCode 기준으로 중복 제거 + 입력 순서 유지
  const normalized = useMemo(
    function () {
      const byCode = new Map();
      const order = new Map();

      teamList.forEach(function (item, index) {
        const code = Number(item?.teamCode);
        if (!code) {
          return;
        }

        if (!byCode.has(code)) {
          byCode.set(code, item);
          order.set(code, index);
        }
      });

      return { byCode, order };
    },
    [teamList],
  );

  const tree = useMemo(
    function () {
      const { byCode, order } = normalized;

      const childrenMap = new Map(); // parentCode -> childCodes[]
      const roots = [];

      function pushChild(parentCode, childCode) {
        if (!childrenMap.has(parentCode)) {
          childrenMap.set(parentCode, []);
        }
        childrenMap.get(parentCode).push(childCode);
      }

      byCode.forEach(function (item, teamCode) {
        const parentCode = Number(item?.departmentCode) || 0;
        const hasParent = parentCode !== 0 && byCode.has(parentCode);

        if (!hasParent) {
          roots.push(teamCode);
          return;
        }
        pushChild(parentCode, teamCode);
      });

      // 입력 순서대로 정렬 (백엔드 응답 순서를 최대한 유지)
      function sortCodes(codes) {
        return codes.sort(function (a, b) {
          return (order.get(a) ?? 0) - (order.get(b) ?? 0);
        });
      }

      roots.sort(function (a, b) {
        return (order.get(a) ?? 0) - (order.get(b) ?? 0);
      });

      childrenMap.forEach(function (codes, parent) {
        childrenMap.set(parent, sortCodes(codes));
      });

      return { roots, childrenMap, byCode };
    },
    [normalized],
  );

  // 기본: 루트 노드는 펼침
  const [expandedSet, setExpandedSet] = useState(new Set());

  useEffect(
    function () {
      setExpandedSet(new Set(tree.roots));
    },
    [tree.roots],
  );

  function toggleExpanded(teamCode) {
    setExpandedSet(function (prev) {
      const next = new Set(prev);
      if (next.has(teamCode)) {
        next.delete(teamCode);
      } else {
        next.add(teamCode);
      }
      return next;
    });
  }

  function handleSelect(teamCode) {
    if (onSelectTeam) {
      onSelectTeam(teamCode);
    }
  }

  function renderNode(teamCode, depth, isLast, ancestorLastFlags) {
    const item = tree.byCode.get(teamCode);
    const label = item?.team || item?.name || '';

    const children = tree.childrenMap.get(teamCode) || [];
    const hasChildren = children.length > 0;
    const isExpanded = expandedSet.has(teamCode);
    const isActive = selectedTeamCode === teamCode;

    return (
      <li key={teamCode} className={styles.node}>
        <div className={styles.nodeRow}>
          {/* 상위 레벨의 세로 라인 */}
          <div className={styles.indentGuides} aria-hidden="true">
            {(() => {
              const guides = [];
              let guidePath = '';

              ancestorLastFlags.forEach(function (ancestorIsLast) {
                guidePath += ancestorIsLast ? '1' : '0';
                guides.push(
                  <span
                    key={`${teamCode}-g-${guidePath}`}
                    className={styles.guide}
                    data-line={ancestorIsLast ? 'off' : 'on'}
                  />,
                );
              });

              return guides;
            })()}
            <span className={styles.branch} data-last={isLast ? 'true' : 'false'}>
              {hasChildren ? (
                <button
                  type="button"
                  className={styles.toggleButton}
                  aria-label={isExpanded ? '접기' : '펼치기'}
                  onClick={function () {
                    toggleExpanded(teamCode);
                  }}
                >
                  <span
                    className={`${styles.toggleIcon} ${isExpanded ? styles.toggleIconExpanded : styles.toggleIconCollapsed}`}
                    aria-hidden="true"
                  />
                </button>
              ) : (
                <span className={styles.toggleSpacer} />
              )}
            </span>
          </div>

          <button
            type="button"
            className={`${styles.labelButton} ${isActive ? styles.labelActive : ''}`}
            onClick={function () {
              handleSelect(teamCode);
            }}
          >
            {label}
          </button>
        </div>

        {hasChildren && isExpanded ? (
          <ul className={styles.children}>
            {children.map(function (childCode, index) {
              return renderNode(childCode, depth + 1, index === children.length - 1, [
                ...ancestorLastFlags,
                isLast,
              ]);
            })}
          </ul>
        ) : null}
      </li>
    );
  }

  // 회사명 클릭 핸들러
  function handleCompanyClick() {
    if (onSelectCompany) {
      onSelectCompany();
    }
  }

  return (
    <div className={styles.container} style={{ height }}>
      <button
        type="button"
        className={`${styles.company} ${isCompanySelected ? styles.companyActive : ''}`}
        onClick={handleCompanyClick}
      >
        {companyName}
      </button>
      <ul className={styles.rootList}>
        {tree.roots.map(function (code, index) {
          return renderNode(code, 0, index === tree.roots.length - 1, []);
        })}
      </ul>
    </div>
  );
}

export default OrganizationTree;
